import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import type { SetStateAction } from "react";

export interface Options<T> {
  key: string;
  serialize: (value: T) => string;
  deserialize: (value: string) => T;
  /** will hydrate localStorage with this, if absent in storage on mount */
  defaultValue: T;
}

export type UseLocalStorage<T> = [T, React.Dispatch<SetStateAction<T>>];

const eventKey = "storage";


export type UseLocalStorageRtrn< T > = {
  value: T;
  setValue: React.Dispatch< SetStateAction< T > >;
  ref: React.MutableRefObject< T >;
}
/**
 * A hook to persist a value in localStorage. So long as the value is updated using this hook,
 * then all consumers will be notified of the change.\
 *
 * NOTE we use refs extensively for two main reasons:
 * - allow this hook to be used like `useLocalStorage({ key: '123', serialize: ... })`\
 * i.e. recreating parameter on each render BUT without huge performance\
 * overhead from creating new callbacks because dependencies changed
 * - to write callback-like utils inside in such a way, that we\
 * are not forced to remount\recreate those, they will always\
 * extract current value from mutable state.
 *
 * It does become messier, but I think it's a small price to pay\
 * for close to none remount\recreation of handlers
 */
export const useLocalStorage = <T>(options: Options<T>): UseLocalStorageRtrn< T > => {
  const { key, defaultValue, deserialize, serialize } = options;


  const keyRef = useRef(key);
  if(keyRef.current !== key) keyRef.current = key;

  const defaultValueRef = useRef(defaultValue);
  if(defaultValueRef.current !== defaultValue) defaultValueRef.current = defaultValue;

  const deserializeRef = useRef(deserialize);
  if(deserializeRef.current !== deserialize) deserializeRef.current = deserialize;

  const serializeRef = useRef(serialize);
  if(serializeRef.current !== serialize) serializeRef.current = serialize;

  /**
   * used to avoid duplicating of same logic "if value in local\
   * storage is null - take default, otherwise parse the one from\
   * local storage" both when we initialize state (and we want to\
   * access localStorage immediately so that we are as precise and\
   * fast as possible, no allowing any delay between "local state\
   * is default value but localStorage holds smth different, need \
   * to propagate that") and when we do useEffect onMount and check\
   * whether local storage value is present (if not - we set it to \
   * default, if yes - we hydrate local state with it)
   */
  const prepareValue = useCallback(
    ( v: string | null ): T => (
      v === null
        ? defaultValueRef.current
        : deserializeRef.current(v)
    ),
    []
  );


  // read value in storage only once and try to use it as initial state
  // (if present), also store that in ref, so that we can use cleaner
  // hydrate localStorage on mount
  const valueInLocalStorage = localStorage.getItem(keyRef.current);
  const valueInLocalStorageRef = useRef(valueInLocalStorage);
  if(valueInLocalStorageRef.current !== valueInLocalStorage) {
    valueInLocalStorageRef.current = valueInLocalStorage;
  }

  /** setup local state either with value in local storage or default one */
  const [value, setValueRaw] = useState( prepareValue( valueInLocalStorage ) );
  const valueRef = useRef(value);
  if(valueRef.current !== value) valueRef.current = value;

  /**
   * effectively what we will expose outside, will not just set\
   * value in state, but also set it to localStorage and emit custom\
   * event so that other subscribers to same key are notified\
   * of change
   */
  const setValue = useCallback< typeof setValueRaw >(valueOrF => {
    const nextNonFunctionValue = valueOrF instanceof Function ? valueOrF(valueRef.current): valueOrF;
    setValueRaw(nextNonFunctionValue);

    const serialized = serializeRef.current(nextNonFunctionValue);

    const curKey = keyRef.current;
    localStorage.setItem(curKey, serialized);

    /** Dispatch custom storage event as the native event only fires on other tabs/windows */
    window.dispatchEvent(new StorageEvent(eventKey, { key: curKey, newValue: serialized }));
  }, []);

  /**
   * create subscription to be able to propagate updates from different places
   */
  useEffect(() => {
    const curKey = keyRef.current;

    const listener = (e: StorageEvent) => {
      if (e.key === curKey && e.newValue) {
        e.preventDefault();
        /**
         * This would fire twice if using another tab/window. Since it would just double set same
         * value, it shouldn't be an issue.
         */
        setValueRaw(deserializeRef.current(e.newValue));
      }
    };

    window.addEventListener(eventKey, listener, false);

    return () => {
      window.removeEventListener(eventKey, listener, false);
    };
  }, []);

  /**
   * if localStorage returns null - hydrate it with default value,/
   * but only once, onMount
   */
  const [ hydratedOnMount, setHydratedOnMount ] = useState(false);
  useEffect(() => {
    if(hydratedOnMount) return;

    setHydratedOnMount(true);

    const localStorageValue = valueInLocalStorageRef.current;
    /**
     * we run setValue, and that will set local state, value in\
     * local storage and emit update, but that's okay, as this will only\
     * happen on mount once for each subscription to local storage\
     * using this hook
     */
    setValue( prepareValue( localStorageValue ) );
  }, [ hydratedOnMount, setValue ]);


  /** useMemo should prevent double render if the value gets set twice. */
  return useMemo(() => ({ value, setValue, ref: valueRef }), [ value, setValue, valueRef ]);
};
